import React, { useState, useEffect, useRef, useMemo } from 'react';
import { isServer } from '@sitecore-jss/sitecore-jss/utils';
import { isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { FeatureFlags } from 'Feature/Enums/FeatureFlag.enum';
import HeaderComponentProps from 'Feature/Header/models/HeaderComponentProps';
import HbtFooterComponent from 'Feature/V2/HbtFooter/components/HbtFooterComponent';
import { HbtFooterProps } from 'Feature/V2/HbtFooter/components/HbtFooterComponent/types';
import HbtHeaderComponent from 'Feature/V2/HbtHeader/components/HbtHeaderComponent';
import HbtSessionTimeoutModalPlaceHolderComponent from 'Feature/V2/HbtSessionTimeoutModalPlaceHolder/components/HbtSessionTimeoutModalPlaceHolderComponent';
import { useFeature } from 'flagged';
import { AuthenticationProvider } from 'Foundation/Authentication';
import { UserTypeAuthorization } from 'Foundation/Authorization';
import { HydrateSitecoreContext } from 'Foundation/HydrateSitecoreContext';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import config from 'temp/config';
import { layoutServiceFactory } from '../../../Foundation/LayoutService/factories/LayoutServiceFactory';
import { SsrState } from './types';

let ssrInitialState: SsrState = null;
const siteLanguages = ['en', 'fr'];
const isBrowser = typeof window !== 'undefined';

const NoSitecoreRouteHandler = ({
  sitecoreContext,
  updateSitecoreContext,
  routeProps,
  children
}: any) => {
  const { t } = useTranslation(['HbtHeader', 'HbtFooter']);
  const isInternalSite = useFeature(FeatureFlags.INTERNAL);
  const headerFields = t('HbtHeader:fields', { returnObjects: true });
  const footerFieldsRaw = t('HbtFooter:fields', {
    returnObjects: true
  }) as HbtFooterProps['fields'];
  const [notFound, setNotFound] = useState(!routeProps);
  const [routeData, setRouteData] = useState(
    ssrInitialState?.sitecore?.route ? ssrInitialState : null
  );
  const [defaultLanguage, setDefaultLanguage] = useState(
    ssrInitialState?.context?.language || config.defaultLanguage
  );

  const componentIsMounted = useRef(false);
  const languageIsChanging = useRef(false);

  useEffect(() => {
    if (ssrInitialState?.sitecore?.route) {
      updateSitecoreContext({
        ...sitecoreContext,
        route: ssrInitialState.sitecore.route,
        itemId: ssrInitialState.sitecore.route.itemId,
        ...ssrInitialState.sitecore.context
      });
    }

    if (ssrInitialState && (!ssrInitialState.sitecore || !ssrInitialState.sitecore.route)) {
      setNotFound(true);
    }

    if (ssrInitialState?.context?.language) {
      setDefaultLanguage(ssrInitialState.context.language);
    }

    if (isBrowser) {
      ssrInitialState = null;
    }

    updateLanguage();
    if (!routeData) {
      updateLayoutData();
    }

    componentIsMounted.current = true;
    return () => {
      componentIsMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const existingRoute = routeProps?.match?.url;
    if (existingRoute === routeProps?.match?.url) return;

    if (isEditorActive()) {
      window.location.assign(routeProps.match.url);
      return;
    }

    updateLanguage();
    updateLayoutData();
  }, [routeProps]);

  const updateLayoutData = async () => {
    let sitecoreRoutePath = routeProps?.match?.params?.sitecoreRoute || '/';
    if (!sitecoreRoutePath.startsWith('/')) {
      sitecoreRoutePath = `/${sitecoreRoutePath}`;
    }

    let language = 'en';
    if (!isServer()) {
      language =
        routeProps?.match?.params?.lang ||
        localStorage.getItem('userSavedLang') ||
        navigator.language.substring(0, 2);
    }

    if (!siteLanguages.includes(language)) {
      language = 'en';
    }

    localStorage.setItem('userSavedLang', language);
    const layoutServiceInstance = layoutServiceFactory.create();
    const fetchedData = await layoutServiceInstance.fetchLayoutData(sitecoreRoutePath, language);

    if (fetchedData?.sitecore?.route) {
      updateSitecoreContext({
        ...sitecoreContext,
        route: fetchedData.sitecore.route,
        itemId: fetchedData.sitecore.route.itemId,
        language,
        ...fetchedData.sitecore.context
      });
      setRouteData(fetchedData as any);
      setNotFound(false);
    } else {
      setRouteData(null);
      setNotFound(true);
    }
  };
  const footerFields = useMemo(() => {
    if (isInternalSite) {
      const { footerText, canadaLogo } = footerFieldsRaw;
      return { footerText, canadaLogo };
    }
    return footerFieldsRaw;
  }, [footerFieldsRaw, isInternalSite]);
  const modalRendering = routeData?.sitecore?.route ?? sitecoreContext.route;
  const updateLanguage = () => {
    let language = navigator.language.substring(0, 2);
    if (!siteLanguages.includes(language)) {
      language = 'en';
    }
    let newLanguage =
      routeProps?.match?.params?.lang || localStorage.getItem('userSavedLang') || language;

    if (i18n.language !== newLanguage) {
      languageIsChanging.current = true;
      i18n.changeLanguage(newLanguage, () => {
        languageIsChanging.current = false;
        if (componentIsMounted.current) {
          setDefaultLanguage(newLanguage);
        }
      });
    }
  };

  if (notFound) {
    return isServer() === false && window.location.pathname !== '/404' ? (
      <Redirect to="/404" />
    ) : null;
  }

  if (!routeData || languageIsChanging.current) {
    return null;
  }

  return (
    <AuthenticationProvider
      Redirect={Redirect}
      sitecoreRouteName={sitecoreContext.route?.name}
      sitecoreContextFactory={{ sitecoreContext, updateSitecoreContext }}
      route={sitecoreContext.route}
    >
      <HydrateSitecoreContext sitecoreContextFactory={{ sitecoreContext, updateSitecoreContext }}>
        <UserTypeAuthorization>
          <HbtHeaderComponent {...({ fields: headerFields } as HeaderComponentProps)} />
          {children}
          {modalRendering && (
            <HbtSessionTimeoutModalPlaceHolderComponent
              rendering={modalRendering}
              route={modalRendering}
            />
          )}
          <HbtFooterComponent fields={footerFields} />
        </UserTypeAuthorization>
      </HydrateSitecoreContext>
    </AuthenticationProvider>
  );
};

export default withSitecoreContext({ updatable: true })(NoSitecoreRouteHandler);

export function setServerSideRenderingState(ssrState: SsrState) {
  ssrInitialState = ssrState;
}
